import { resolveUserDisplayName } from "../../../../utils/userIdentity";

const normalizeIdentifier = (value) => {
    if (value === undefined || value === null) {
        return null;
    }
    const normalized = String(value).trim();
    return normalized || null;
};

const normalizeBandRole = (role) => (
    String(role).trim().toUpperCase() === "OWNER" ? "OWNER" : "MEMBER"
);

export const resolveLinkedBandObjectId = (...sources) => {
    for (const source of sources) {
        const candidate = typeof source === "string"
            ? source
            : source?.linkedBandObjectId;
        const normalized = normalizeIdentifier(candidate);
        if (normalized) {
            return normalized;
        }
    }
    return null;
};

const toWorkspaceMember = (member) => {
    const userId = normalizeIdentifier(member?.userId);
    if (!userId) {
        return null;
    }
    const isPending = String(member?.status).trim().toUpperCase() === "PENDING";
    return {
        membershipId: userId,
        userId,
        role: normalizeBandRole(member?.role),
        status: isPending ? "INVITED" : "ACTIVE",
        invitedAt: member?.joinedAt ?? null,
        joinedAt: isPending ? null : (member?.joinedAt ?? null),
        lastSeenAt: null,
        user: member?.user ?? null,
    };
};

const toWorkspaceInvite = (member) => {
    const userId = normalizeIdentifier(member?.userId);
    if (!userId) {
        return null;
    }
    return {
        id: userId,
        email: null,
        displayName: resolveUserDisplayName(member?.user),
        targetUserId: userId,
        role: normalizeBandRole(member?.role),
        status: "PENDING",
        invitedAt: member?.joinedAt ?? null,
        expiresAt: null,
    };
};

export const mapBandDetailsToWorkspaceMembershipState = (bandDetails) => {
    const rawMembers = Array.isArray(bandDetails?.members) ? bandDetails.members : [];
    const activeMembers = rawMembers
        .filter((member) => String(member?.status).trim().toUpperCase() === "ACTIVE")
        .map(toWorkspaceMember)
        .filter(Boolean);
    const pendingInvites = rawMembers
        .filter((member) => String(member?.status).trim().toUpperCase() === "PENDING")
        .map(toWorkspaceInvite)
        .filter(Boolean);

    return {
        members: activeMembers,
        invites: pendingInvites,
        activeMemberCount: typeof bandDetails?.memberCount === "number"
            ? bandDetails.memberCount
            : activeMembers.length,
    };
};

export const mergeBandMembershipIntoWorkspaceDetails = (workspaceDetails, bandDetails) => {
    if (!workspaceDetails || typeof workspaceDetails !== "object") {
        return workspaceDetails;
    }
    const membershipState = mapBandDetailsToWorkspaceMembershipState(bandDetails);
    return {
        ...workspaceDetails,
        members: membershipState.members,
        activeMemberCount: membershipState.activeMemberCount,
    };
};

export const shouldAutoSelectProvisionedWorkspace = ({ currentSelectedGroupId, selectionGuardGroupId } = {}) => {
    const currentId = normalizeIdentifier(currentSelectedGroupId);
    const guardId = normalizeIdentifier(selectionGuardGroupId);
    if (!guardId) {
        return currentId === null;
    }
    return currentId === guardId;
};
